import { supabase } from "@/integrations/supabase/client";
import { buildResumoItensValor, getValorTotalSinistro, parseItensValorFromSinistro } from "@/lib/sinistroValores";

/** Helpers para criar conta a pagar a partir de um sinistro finalizado. */

export type SinistroParaDespesa = {
  id: number | string;
  cliente_id?: number;
  projeto_id?: number | string | null;
  tipo?: string | null;
  descricao?: string | null;
  itens_valor?: unknown;
  anexos?: string[] | null;
  valor_estimado?: number | string | null;
  valor_aprovado?: number | string | null;
  cliente?: { nome?: string } | null;
};

export function getValorSinistroParaDespesa(sinistro: SinistroParaDespesa): number | undefined {
  return getValorTotalSinistro(sinistro);
}

/** Vencimento no mês atual para aparecer no filtro padrão do Financeiro (mês corrente). */
export function dataVencimentoPadraoSinistro(): string {
  const hoje = new Date();
  const y = hoje.getFullYear();
  const m = String(hoje.getMonth() + 1).padStart(2, "0");
  const ultimoDia = new Date(y, hoje.getMonth() + 1, 0).getDate();
  return `${y}-${m}-${String(ultimoDia).padStart(2, "0")}`;
}

export function buildObservacoesDespesaSinistro(
  sinistro: SinistroParaDespesa,
  getTipoLabel: (tipo: string) => string
): string {
  const partes: string[] = [`Sinistro #${sinistro.id}`];
  if (sinistro.tipo) partes.push(getTipoLabel(sinistro.tipo));
  if (sinistro.cliente?.nome) partes.push(sinistro.cliente.nome);
  const itens = parseItensValorFromSinistro(sinistro);
  const resumoItens = buildResumoItensValor(itens);
  if (resumoItens) {
    partes.push(resumoItens.length > 220 ? `${resumoItens.slice(0, 220)}…` : resumoItens);
  } else if (sinistro.descricao?.trim()) {
    const desc = sinistro.descricao.trim();
    partes.push(desc.length > 180 ? `${desc.slice(0, 180)}…` : desc);
  }
  return partes.join(" — ");
}

export function dadosIniciaisDespesaFromSinistro(
  sinistro: SinistroParaDespesa,
  getTipoLabel: (tipo: string) => string
) {
  const valor = getValorSinistroParaDespesa(sinistro);
  return {
    sinistroId: Number(sinistro.id),
    clienteId: sinistro.cliente_id ? Number(sinistro.cliente_id) : undefined,
    valor,
    observacoes: buildObservacoesDespesaSinistro(sinistro, getTipoLabel),
    dataVencimento: dataVencimentoPadraoSinistro(),
    titulo: `Conta a pagar — Sinistro #${sinistro.id}`,
  };
}

export async function buscarDespesaPorSinistro(sinistroId: number): Promise<{ id: number } | null> {
  const { data, error } = await supabase
    .from("transacoes_financeiras")
    .select("id")
    .eq("sinistro_id", sinistroId)
    .eq("tipo", "saida")
    .limit(1)
    .maybeSingle();

  if (error) {
    if (error.code === "PGRST204" && error.message?.includes("sinistro_id")) {
      return null;
    }
    throw error;
  }
  return data;
}

export async function obterFormaPagamentoPadrao(): Promise<string> {
  const { data, error } = await supabase
    .from("formas_pagamento")
    .select("nome")
    .eq("ativo", true)
    .order("nome")
    .limit(1)
    .maybeSingle();

  if (error || !data?.nome) return "A definir";
  return data.nome;
}

/** Cria conta a pagar vinculada ao sinistro (idempotente por sinistro_id). */
export async function criarDespesaAutomaticaFromSinistro(
  sinistro: SinistroParaDespesa,
  getTipoLabel: (tipo: string) => string
): Promise<{ criada: boolean; transacaoId?: number; motivo?: string }> {
  const sinistroId = Number(sinistro.id);
  const valor = getValorSinistroParaDespesa(sinistro);

  if (!valor) {
    return { criada: false, motivo: "sem_valor" };
  }

  const existente = await buscarDespesaPorSinistro(sinistroId);
  if (existente) {
    return { criada: false, motivo: "ja_existe", transacaoId: existente.id };
  }

  const formaPagamento = await obterFormaPagamentoPadrao();
  const observacoes = buildObservacoesDespesaSinistro(sinistro, getTipoLabel);

  const payload = {
    sinistro_id: sinistroId,
    cliente_id: sinistro.cliente_id ? Number(sinistro.cliente_id) : null,
    tipo: "saida" as const,
    valor,
    forma_pagamento: formaPagamento,
    data_vencimento: dataVencimentoPadraoSinistro(),
    observacoes,
    status: "pendente" as const,
  };

  const tryInsert = async (includeSinistroId: boolean) => {
    const { sinistro_id, ...rest } = payload;
    return supabase
      .from("transacoes_financeiras")
      .insert({
        ...rest,
        ...(includeSinistroId ? { sinistro_id } : {}),
      })
      .select("id")
      .single();
  };

  let { data, error } = await tryInsert(true);

  if (error?.code === "PGRST204" && error.message?.includes("sinistro_id")) {
    ({ data, error } = await tryInsert(false));
  }

  if (error) throw error;
  return { criada: true, transacaoId: data?.id };
}

/** Converte string monetária brasileira para número ou undefined. */
export function parseValorMonetarioBr(valor: string): number | undefined {
  const s = (valor ?? "").trim();
  if (!s) return undefined;
  const limpo = s.replace(/[R$\s]/g, "").replace(/\./g, "").replace(",", ".");
  const n = parseFloat(limpo);
  return !isNaN(n) && n > 0 ? n : undefined;
}
